<template>
  <div class="page-shell">
    <AppSearchField v-model="searchQuery" placeholder="Search name, zip, city..." />

    <div class="count-label">{{ filteredCompanies.length }} ASSOCIATIONS AVAILABLE</div>

    <div v-if="errorMessage" class="error-banner">
      {{ errorMessage }}
    </div>

    <AppTab v-model="activeTab" :items="tabItems" />

    <div v-if="isLoading" class="loading-row">
      <v-progress-circular indeterminate size="18" width="2" />
      <span>Loading associations...</span>
    </div>

    <template v-else>
      <AppCard
        v-for="company in filteredCompanies"
        :key="company.id"
        class="association-card"
        role="button"
        tabindex="0"
        @click="handleOpen(String(company.id))"
      >
        <div class="association-card__top">
          <div class="association-card__icon" aria-hidden="true">
            <v-icon size="22" color="#e4572e">mdi-bank-outline</v-icon>
          </div>

          <div class="association-card__copy">
            <!-- <div class="association-card__kicker">ASSOCIATION</div> -->
            <div class="association-card__title">{{ company.name }}</div>
            <div class="association-card__meta-inline">
              <div class="association-card__meta-stack">
                <div class="association-card__meta-row">
                  <v-icon size="14" color="#64748b">mdi-map-marker-outline</v-icon>
                  <span class="association-card__location">
                    {{ company.address }}
                  </span>
                </div>
                <div v-if="company.address?.distance" class="association-card__distance">
                  {{ company.address.distance }}
                </div>
              </div>
            </div>
            <!-- <div v-if="company.description" class="association-card__subtitle">
              {{ company.description }}
            </div> -->
            <!-- <div class="py-2" v-if="company.application_status === 'not_applied'">
              <AppButtonFlat variant="outlined" block append-icon="mdi-arrow-right">Apply Now </AppButtonFlat>
            </div> -->
          </div>

          <v-icon
            class="association-card__chev"
            size="18"
            color="#94a3b8"
            v-if="company.application_status !== 'not_applied'"
          >
            mdi-chevron-right
          </v-icon>
        </div>
      </AppCard>

      <AppCard v-if="!filteredCompanies.length && !errorMessage" class="empty-card">
        No associations available right now.
      </AppCard>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import AppCard from "@/shared/components/ui/AppCard.vue";
import { fetchCompanies } from "@/modules/vendor/services/vendors.api";
import AppSearchField from "@/shared/components/ui/AppSearchField.vue";
import AppTab from "@/shared/components/ui/AppTab.vue";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";

type CompanyAddress = {
  city: string;
  state: string;
  distance: string;
};
const tabItems = [
  { label: "Listings", value: "not_applied" },
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
];
type CompanyItem = {
  name: string;
  id: number;
  description: string;
  application_status?: string;
  application_id?: number | null;
  address: Partial<CompanyAddress> | null;
};

const router = useRouter();
const isLoading = ref(false);
const errorMessage = ref("");
const companies = ref<CompanyItem[]>([]);
const searchQuery = ref("");
const activeTab = ref(tabItems[0].value);

const filteredCompanies = computed(() => {
  const status = String(activeTab.value || "").trim();
  const byStatus = status
    ? companies.value.filter(
        (c) => String(c.application_status || "not_applied") === status
      )
    : companies.value;

  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return byStatus;
  return byStatus.filter((c) => {
    const name = String(c.name || "").toLowerCase();
    const desc = String(c.description || "").toLowerCase();
    const city = String(c.address?.city || "").toLowerCase();
    const state = String(c.address?.state || "").toLowerCase();
    return name.includes(q) || desc.includes(q) || city.includes(q) || state.includes(q);
  });
});

function formatLocation(city?: string, state?: string) {
  const c = String(city || "").trim();
  const s = String(state || "").trim();
  if (c && s) return `${c}, ${s}`;
  return c || s || "—";
}

onMounted(() => {
  void loadCompanies();
});

async function loadCompanies() {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    const response: any = await fetchCompanies();
    const list = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
      ? response.data
      : [];
    companies.value = (list || []) as CompanyItem[];
  } catch (error) {
    console.error(error);
    errorMessage.value = "Unable to load associations. Please try again.";
    companies.value = [];
  } finally {
    isLoading.value = false;
  }
}

function handleOpen(id: string) {
  router.push({ name: "vendor.company.detail", params: { id } });
}
</script>

<style scoped>
.page-shell {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px !important;
}

.search-input :deep(.v-field) {
  border-radius: 14px;
}

.count-label {
  font-size: 12px;
  letter-spacing: 0.08em;
  font-weight: 800;
  color: rgba(15, 23, 42, 0.6);
  padding: 0 2px;
}

.loading-row {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--color-subtitle);
  font-size: 14px;
}

.error-banner {
  background: rgba(239, 68, 68, 0.08);
  color: #b91c1c;
  border: 1px solid rgba(239, 68, 68, 0.18);
  padding: 10px 12px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
}

.association-card {
  padding: 14px;
  border-radius: 14px;
  cursor: pointer;
}

.association-card__top {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.association-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(228, 87, 46, 0.12);
  display: grid;
  place-items: center;
  flex: 0 0 auto;
}

.association-card__copy {
  min-width: 0;
  flex: 1 1 auto;
}

.association-card__kicker {
  font-size: 10px;
  letter-spacing: 0.08em;
  font-weight: 800;
  color: #e4572e;
  margin-bottom: 2px;
}

.association-card__title {
  font-weight: 600;
  font-size: 14px;
  color: #0f172a;
  line-height: 1.2;
  text-transform: capitalize;
}

.association-card__meta-inline {
  margin-top: 6px;
  display: inline-flex;
  align-items: flex-start;
  font-size: 13px;
  color: #0f172a;
  min-width: 0;
}

.association-card__meta-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.association-card__meta-row {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.association-card__location {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.association-card__subtitle {
  margin-top: 3px;
  font-size: 13px;
  color: var(--color-subtitle);
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
}

.association-card__chev {
  flex: 0 0 auto;
  margin-top: 2px;
}

.association-card__bottom {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.association-card__meta-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #0f172a;
  /* font-weight: 500; */
  min-width: 0;
}

.association-card__distance {
  color: #e4572e;
  font-weight: 500;
  font-size: 12px;
  white-space: nowrap;
}

.empty-card {
  padding: 14px;
  border-radius: 14px;
  color: #64748b;
  font-weight: 600;
}
</style>
